/**
 * RGM Starter Studio · Waveform lane component.
 *
 * Phase 1B-live additions:
 *   • `imgSrc` prop — renders a Cloudinary `fl_waveform` PNG as the
 *     lane background (real audio data, server-decoded) so beats
 *     whose containers iOS Safari's `decodeAudioData` cannot parse
 *     still show a real waveform.
 *   • Peaks[]-driven SVG still overlays when available (client-side
 *     colour-tinted) so vocal lanes remain colour-identifiable.
 */
import React from "react";

export const LANE_PALETTE = {
  fuchsia: { text: "text-fuchsia-500/85", textActive: "text-fuchsia-300", swatch: "bg-fuchsia-500", ring: "border-fuchsia-400/70", tint: "bg-fuchsia-500/[0.08]", hex: "d946ef" },
  cyan:    { text: "text-cyan-500/85",    textActive: "text-cyan-300",    swatch: "bg-cyan-500",    ring: "border-cyan-400/70",    tint: "bg-cyan-500/[0.08]",    hex: "06b6d4" },
  amber:   { text: "text-amber-500/85",   textActive: "text-amber-300",   swatch: "bg-amber-500",   ring: "border-amber-400/70",   tint: "bg-amber-500/[0.08]",   hex: "f59e0b" },
  emerald: { text: "text-emerald-500/85", textActive: "text-emerald-300", swatch: "bg-emerald-500", ring: "border-emerald-400/70", tint: "bg-emerald-500/[0.08]", hex: "10b981" },
  violet:  { text: "text-violet-500/85",  textActive: "text-violet-300",  swatch: "bg-violet-500",  ring: "border-violet-400/70",  tint: "bg-violet-500/[0.08]",  hex: "8b5cf6" },
};

export function laneColorTokens(color) {
  return LANE_PALETTE[color] || LANE_PALETTE.violet;
}

export function WaveformLane({ peaks, height = 44, playhead01 = 0, active = false, empty, color, imgSrc }) {
  const N = peaks?.length || 0;
  const tokens = laneColorTokens(color);
  const hasPeaks = !empty && N > 0;
  const hasImg = !empty && !!imgSrc;

  if (!hasPeaks && !hasImg) {
    return (
      <div className="relative w-full rounded bg-white/[0.02] border border-dashed border-white/10 flex items-center justify-center"
        style={{ height }} data-testid="wave-empty">
        <p className="text-[10px] text-gray-500 uppercase tracking-widest">no audio yet</p>
        <PlayheadLine pos01={playhead01} />
      </div>
    );
  }
  return (
    <div className="relative w-full rounded overflow-hidden" style={{ height }} data-testid="wave-lane">
      {hasImg && (
        <img
          src={imgSrc}
          alt=""
          crossOrigin="anonymous"
          className="absolute inset-0 w-full h-full object-fill opacity-90 pointer-events-none"
          data-testid="wave-img"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      )}
      {hasPeaks && (
        <svg
          viewBox={`0 0 ${N} 100`}
          preserveAspectRatio="none"
          className={`relative w-full h-full ${active ? tokens.textActive : tokens.text}`}
        >
          {peaks.map((p, i) => {
            const h = Math.max(2, (p / 255) * 92);
            const y = 50 - h / 2;
            return <rect key={i} x={i + 0.15} y={y} width={0.7} height={h} fill="currentColor" />;
          })}
        </svg>
      )}
      <PlayheadLine pos01={playhead01} />
    </div>
  );
}

function PlayheadLine({ pos01 }) {
  const p = Math.max(0, Math.min(1, pos01 || 0));
  return (
    <div
      className="absolute top-0 bottom-0 w-[2px] bg-white/90 shadow-[0_0_6px_rgba(255,255,255,0.6)] pointer-events-none"
      style={{ left: `${p * 100}%` }}
      data-testid="wave-playhead"
    />
  );
}

export function TimelineRuler({ durationMs, playheadMs, height = 20, onSeek }) {
  const dur = Math.max(1000, durationMs || 60000);
  const totalSeconds = Math.ceil(dur / 1000);
  const step = totalSeconds <= 30 ? 5 : totalSeconds <= 90 ? 10 : totalSeconds <= 180 ? 15 : 30;
  const ticks = [];
  for (let s = 0; s <= totalSeconds; s += step) {
    ticks.push({ s, left: (s * 1000) / dur });
  }
  const playhead01 = Math.max(0, Math.min(1, (playheadMs || 0) / dur));
  const handlePointer = (e) => {
    if (!onSeek) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ("touches" in e && e.touches[0]) ? e.touches[0].clientX - rect.left : e.clientX - rect.left;
    const frac = Math.max(0, Math.min(1, x / rect.width));
    onSeek(Math.round(frac * dur));
  };
  return (
    <div
      className={`relative w-full text-[10px] text-gray-400 select-none ${onSeek ? "cursor-pointer" : ""}`}
      style={{ height }}
      data-testid="timeline-ruler"
      onClick={handlePointer}
    >
      <div className="absolute inset-0 border-b border-white/10" />
      {ticks.map((t) => (
        <div
          key={t.s}
          className="absolute top-0 bottom-0 border-l border-white/10 pl-1"
          style={{ left: `${t.left * 100}%` }}
        >
          {t.s === 0 ? null : <span>{formatSeconds(t.s)}</span>}
        </div>
      ))}
      <div
        className="absolute top-0 bottom-0 w-[2px] bg-white/90 pointer-events-none"
        style={{ left: `${playhead01 * 100}%` }}
      />
    </div>
  );
}

export function formatSeconds(s) {
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${r}`;
}

/**
 * Live real-time input level meter. Fed by `onLevel` from
 * StudioEngine.startRecording — pure metering, does not alter the
 * recorded signal.
 */
export function InputMeter({ level = 0, color = "fuchsia" }) {
  const tokens = laneColorTokens(color);
  const pct = Math.max(0, Math.min(100, (level / 255) * 100));
  const clipping = pct > 92;
  return (
    <div className="w-24 h-2 rounded-full bg-white/10 overflow-hidden" data-testid="mic-input-meter" aria-label="Microphone input level">
      <div
        className={`h-full transition-[width] duration-75 ${clipping ? "bg-red-500" : tokens.swatch}`}
        style={{ width: `${pct}%` }}
        data-testid="mic-input-meter-fill"
      />
    </div>
  );
}
